import React, { useEffect, useState } from 'react';
import { Loading } from '@components/index';
import { GoodfyndSitesSeoHead } from '@features/goodfynd-sites/components/goodfynd-sites-seo-head';
import {
  GoodfyndSites404,
  GoodfyndSites500,
  GoodfyndSitesPage,
} from '@features/goodfynd-sites/pages/goodfynd-sites-page';
import {
  getGoodfyndSitesData,
  type GoodfyndSitesData,
  isGoodfyndSitesPreview,
} from '@features/goodfynd-sites/utils/goodfynd-sites-util';
import { GoodfyndSiteProvider } from 'features/goodfynd-sites/context/goodfynd-site-context';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Head from 'next/head';

type GoodfyndSitesProps =
  | GoodfyndSitesData
  | {
      hasError: true;
      organization?: never;
      vendor?: never;
      siteUrl?: never;
      goodfyndSiteSeo?: never;
    };

function GoodfyndSites(props: GoodfyndSitesProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [{ goodfyndSiteSeo, organization }, setGoodfyndSiteData] =
    useState<GoodfyndSitesProps>(props);

  useEffect(() => {
    if (goodfyndSiteSeo && organization) {
      return;
    }

    (async () => {
      try {
        setIsLoading(true);
        const data = await getGoodfyndSitesData();
        setGoodfyndSiteData({
          goodfyndSiteSeo: data.goodfyndSiteSeo,
          organization: data.organization,
        });
      } catch (error) {
        console.error('error getting organization', error);
        setGoodfyndSiteData({ hasError: true });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [goodfyndSiteSeo, organization]);

  if (isLoading) {
    return <Loading type="line" />;
  }

  if ('hasError' in props && props.hasError) {
    return <GoodfyndSites500 />;
  }

  if (!goodfyndSiteSeo || !organization) {
    return <GoodfyndSites404 />;
  }

  const isPreview = isGoodfyndSitesPreview();
  if (!isPreview && !organization.published) {
    return <GoodfyndSites404 />;
  }

  return (
    <>
      <Head>
        <title key="goodfynd-sites:title">{goodfyndSiteSeo.title}</title>
        <GoodfyndSitesSeoHead seo={goodfyndSiteSeo} />
      </Head>

      <GoodfyndSiteProvider organization={organization}>
        <GoodfyndSitesPage />
      </GoodfyndSiteProvider>
    </>
  );
}

GoodfyndSites.getLayout = function getLayout(page: React.ReactElement) {
  return page;
};

// Disable static rendering

export const getServerSideProps = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<GoodfyndSitesProps>> => {
  try {
    const data = await getGoodfyndSitesData(context);
    return { props: data };
  } catch (error) {
    console.error('error getting organization', error);
    return { props: { hasError: true } };
  }
};

export default GoodfyndSites;
